/**
 * PrimaryNav — the shell's primary-navigation landmark, rendered as a
 * fixed bottom nav with icon + label per column. Three Cluster-1 route
 * entries in DOM + Tab order: Koti, Viestit, Omat tiedot (per FR-001
 * amended via D-048 and 026 US1, which retired the Ilmoitukset entry to
 * match the current prototype's three-item nav), followed by a 4th
 * **"Valikko"** entry — a menu trigger `<button>` (NOT a route link)
 * that opens the `MenuSheet` (item-1 menu-position re-baseline, D-130,
 * FR-001 + FR-056: the proto moves the menu affordance out of the TopBar
 * into the bottom action cluster). The trigger calls the `onOpenMenu`
 * handler `Layout` wires to its `openMenu`.
 *
 * **Bottom-nav layout.** Fixed to the viewport bottom
 * (`fixed bottom-0 left-0 right-0 z-30`), height pinned via the
 * `--spacing-bottom-nav` token (`h-bottom-nav`). The inner grid columns
 * are `grid-cols-4` inside a `max-w-md mx-auto` wrapper so on wider
 * viewports the mobile-shell posture (per the brief) stays centred. The
 * border-top + `bg-card` surface sits the nav above the body
 * background without floating.
 *
 * **Nav-source decision, hard-coded Cluster-1 nav entries.**
 * `apps/web/src/shell/routes.ts` ships `ROUTES` populated with the full
 * route set, which includes routes (`/login`, `/admin`, `/account`
 * redirect) that do NOT appear in the primary nav. Keeping the three
 * bottom-nav entries hard-coded (`NAV_ENTRIES` in `./navEntries.ts`,
 * shared with the desktop sidebar) decouples the component from
 * route-registry layout and makes the UX contract explicit: the three
 * entries are the primary-nav contract and nothing more.
 *
 * **Icons come from the in-tree `./icons.js` module.** No `lucide-react`
 * (D-043). Each icon is a minimal inline SVG sized to `h-6 w-6`; `stroke`
 * resolves through `currentColor` so the `text-tab-active` /
 * `text-muted-foreground` utilities on the surrounding `NavLink` drive
 * the tint without per-icon colour coupling.
 *
 * **Active + inactive colouring via tokens.** Active paints
 * `text-tab-active` (the prototype's deep-green active-nav tint resolved
 * through `--color-tab-active`); inactive paints `text-muted-foreground`
 * and picks up `text-foreground` on hover. No bracketed
 * `text-[#…]` literals (FR-047, FR-002). Focus-visible reads the
 * sanctioned `ring-ring` focus treatment so keyboard users see the same
 * affordance every other Cluster-1 control uses (FR-033).
 *
 * **Visibility across session states.** The nav is rendered
 * unconditionally by callers. A logged-out visitor is walled to
 * `/login` (`RouteGuard`), but because the shell chrome wraps the guard
 * (chrome outermost — see `RouteRegistry`), an anonymous visitor on a
 * protected route briefly mounts this nav for the redirect frame; its
 * capability entries grey during that bounce (see `useNavItemGating`)
 * so they never flash as active links. `Layout` hides the whole
 * component on `/login` so the sign-in frame reads clean (the brief's
 * "hide bottom nav on /login" rule); because the Valikko trigger lives
 * here, hiding the nav on `/login` is also what keeps the menu trigger
 * off the sign-in frame.
 *
 * **`aria-current="page"`** is emitted by `<NavLink>` automatically on
 * the matching entry; we do not set it manually.
 */

import type { ComponentType, JSX, SVGProps } from 'react';
import { NavLink } from 'react-router';
import { useTranslate } from '../i18n/useTranslate.js';
import { useUnreadCount } from '../member-cases/useUnreadCount.js';

import { MenuIcon } from './icons.js';
import { NAV_ENTRIES, useNavItemGating } from './navEntries.js';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

const navClasses = 'fixed bottom-0 left-0 right-0 z-30 bg-card border-t border-border';
const listClasses = 'mx-auto grid h-bottom-nav max-w-md grid-cols-4';
// Each NavLink renders as an anchor (browsers default to pointer
// cursor). `transition` + `active:scale-95` gives the press-down
// feedback proto-parity calls for; the existing `text-tab-active` /
// `text-muted-foreground` colour transitions still apply.
const baseLinkClasses =
  'flex flex-col items-center justify-center gap-1 px-2 text-xs font-medium no-underline ' +
  'transition active:scale-95 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const activeLinkClasses = 'text-tab-active';
const inactiveLinkClasses = 'text-muted-foreground hover:text-foreground';
// Greyed, non-navigable entry for a capability the active member's state does
// not allow (028-membership-state-gating, AC4 / FR-010).
const disabledLinkClasses = 'text-muted-foreground/50 cursor-not-allowed';
// Unread-count pill anchored to the icon's top-right corner (030 US2, the
// proto's BottomNav badge idiom on shell tokens — no raw hex, FR-047).
// `bg-brand-deep` is the elevated-chrome token the floating pill already
// uses; the count is visually compact, the accessible text is the sr-only
// label beside it.
const badgeClasses =
  'absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full ' +
  'bg-brand-deep px-1 text-xs font-semibold leading-none text-brand-deep-foreground';

interface EntryContentProps {
  readonly Icon: IconComponent;
  readonly label: string;
  /** > 0 renders the unread pill (zero-hide); only the Viestit entry passes it. */
  readonly unreadCount: number;
  /** Authored a11y chrome (research D6): the sr-only badge label with the count. */
  readonly unreadLabel: string;
}

/** Icon + label (+ the Viestit unread pill) shared by the link and greyed variants. */
function EntryContent({ Icon, label, unreadCount, unreadLabel }: EntryContentProps): JSX.Element {
  return (
    <>
      <span className="relative">
        <Icon aria-hidden="true" className="h-6 w-6" />
        {unreadCount > 0 ? (
          <span className={badgeClasses}>
            <span aria-hidden="true">{unreadCount}</span>
            <span className="sr-only">{unreadLabel}</span>
          </span>
        ) : null}
      </span>
      <span>{label}</span>
    </>
  );
}

function composeLinkClassName({ isActive }: { isActive: boolean }): string {
  const stateClasses = isActive ? activeLinkClasses : inactiveLinkClasses;
  return `${baseLinkClasses} ${stateClasses}`;
}

export interface PrimaryNavProps {
  /**
   * Opens the menu sheet. Item-1 menu-position re-baseline: the menu
   * trigger is the 4th bottom-nav "Valikko" entry (the proto moves it out
   * of the TopBar), and `Layout` owns the open/close state, so it passes
   * its `openMenu` here. Wiring the handler in `Layout` keeps the trigger
   * and the `MenuSheet` body as siblings under one owner.
   */
  readonly onOpenMenu: (trigger: HTMLButtonElement) => void;
}

export function PrimaryNav({ onOpenMenu }: PrimaryNavProps): JSX.Element {
  const t = useTranslate();
  // A greyed entry stays in the DOM with `aria-disabled` so the disabled state
  // is perceivable and announced, not silently removed (FR-010). The rules are
  // shared with the menu sheet and the desktop sidebar (`useNavItemGating`).
  const isGreyed = useNavItemGating();
  // Unread badge (030 US4 / 050 US3, FR-009). Wired to the REAL aggregate
  // `unreadConversationCount` from the 1.4.0 list envelope (`member.cases.list`),
  // via the non-suspending `useUnreadCount` hook — the demo-fixture inbox that
  // used to feed this is retired (D-081). Zero-hide: a missing port / unavailable
  // read / no-unread all yield 0 → the badge is hidden, never fabricated, never
  // an N+1 detail walk (refuse-don't-fake, P7).
  const unreadCount = useUnreadCount();
  return (
    <nav aria-label={t('shell.primaryNav.label')} className={navClasses}>
      {/* Polite live region: the badge count is in the Viestit link's accessible
          name (static), but a change while focus is elsewhere — e.g. the count
          decrementing after a reply is marked read and the member navigates —
          would otherwise be silent. This announces the current unread count on
          change; empty at zero so clearing the badge announces nothing. */}
      <span role="status" className="sr-only">
        {unreadCount > 0
          ? t('shell.primaryNav.unreadBadge').replace('{n}', String(unreadCount))
          : ''}
      </span>
      <div className={listClasses}>
        {NAV_ENTRIES.map((entry) => {
          const { Icon } = entry;
          const disabled = isGreyed({ source: 'nav', entry });
          // The unread pill belongs to the Viestit entry only (zero-hide).
          const entryUnread = entry.path === '/messages' ? unreadCount : 0;
          const content = (
            <EntryContent
              Icon={Icon}
              label={t(entry.labelKey)}
              unreadCount={entryUnread}
              unreadLabel={t('shell.primaryNav.unreadBadge').replace('{n}', String(entryUnread))}
            />
          );
          if (disabled) {
            // Greyed entry as a focusable `<button aria-disabled>` (a semantic,
            // natively focus-reachable control): keyboard-only / switch users
            // land on it and the AT announces the disabled state, rather than
            // Tabbing past it as if removed (FR-010, M1). It does not navigate
            // (no handler); the route guard refuses it anyway.
            return (
              <button
                key={entry.path}
                type="button"
                aria-disabled="true"
                className={`${baseLinkClasses} ${disabledLinkClasses}`}
              >
                {content}
              </button>
            );
          }
          return (
            <NavLink
              key={entry.path}
              to={entry.path}
              end={entry.path === '/'}
              className={composeLinkClassName}
            >
              {content}
            </NavLink>
          );
        })}
        {/* 4th slot — the "Valikko" menu trigger (item-1 menu-position
            re-baseline, D-130 / FR-001 + FR-056). A `<button>`, NOT a
            NavLink: it opens the MenuSheet drawer rather than navigating,
            so it carries no route and never takes the active-nav tint.
            The label reuses `shell.menu.title` ("Valikko") — no new copy.
            Always present for the audiences that see the bottom nav; a
            logged-out visitor only mounts it for the brief redirect frame
            before `RouteGuard` walls them to `/login`, where `Layout`
            hides the whole nav. */}
        <button
          type="button"
          onClick={(event) => onOpenMenu(event.currentTarget)}
          className={`${baseLinkClasses} ${inactiveLinkClasses}`}
        >
          <EntryContent
            Icon={MenuIcon}
            label={t('shell.menu.title')}
            unreadCount={0}
            unreadLabel=""
          />
        </button>
      </div>
    </nav>
  );
}
